import { html, nothing, css, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ref, createRef } from 'lit/directives/ref.js';
import { FormValidationController } from '@/controllers/FormValidationController';
import { live } from 'lit/directives/live.js';
import stylesInput from '../style/input.scss?inline';
import styles from './input.scss?inline';
import SotbitInputStyle from '../style/index';
import { checkValidity } from '@/components/input/common.js';

export default class SotbitInputGroup extends SotbitInputStyle {
    static formAssociated = true;

    static styles = css`
        ${unsafeCSS(stylesInput)}
        ${unsafeCSS(styles)}
    `;

    static properties = {
        type: { type: String },
        name: { type: String },
        label: { type: String },
        placeholder: { placeholder: String },
        value: { type: String, reflect: true },
        required: { type: Boolean },
        disabled: { type: Boolean },
        autofocus: { type: Boolean },
        autocomplete: { type: String },
        error: { type: Boolean },
        readonly: { type: Boolean, reflect: true },
        size: { type: String },
        button: { type: Boolean },
        min: { type: String },
        btnTitle: { type: String, attribute: 'btn-title' },
        initValue: { type: String, attribute: 'init-value' },
    };

    constructor() {
        super();

        this._internals = this.attachInternals();
        this._validationController = new FormValidationController(this, {
            getValue: () => this.value,
            getInputRef: () => this.inputRef,
        });

        this._value = '';
        this.name = '';
        this.label = null;
        this.placeholder = null;
        this.required = false;
        this.disabled = false;
        this.autofocus = false;
        this.autocomplete = 'off';
        this.inputRef = createRef();
        this.styleRef = createRef();
        this.size = null;
        this.error = false;
        this._defaultValue = '';
        this.step = null;
        this.button = null;
        this._form = null;
    }

    set value(value) {
        const preparedValue = this.valueNumberFormat(value);

        if (this.inputRef?.value) {
            this.inputRef.value.value = preparedValue;
        }

        this._internals?.setFormValue(preparedValue);
        this._value = preparedValue;

        if (this.inputRef?.value) {
            this._validationController.validate();
        }
    }

    get value() {
        return this._value;
    }

    get form() {
        return this._form;
    }

    set form(form) {
        this._validationController.removeInputHandlers();
        this._internals?.setFormValue(this.value);
        this._form = form;
        this._validationController.validate();
        this._validationController.setInputHandlers();
    }

    get placeholder() {
        return this._placeholder;
    }

    set placeholder(placeholder) {
        this._placeholder = placeholder;
    }

    get selectionStart() {
        return this.inputRef.value?.selectionStart || 0;
    }

    valueNumberFormat(value) {
        const valueString = typeof value !== 'string' ? value.toString() : value;
        let prepared = valueString.replace(/[^0-9\s,\.]/g, '');
        prepared = prepared.replace(/,/g, '.');
        const regexDig = /[1-9]/;

        if (valueString.startsWith('-')) {
            prepared = '-' + prepared;
        }

        if (valueString.length > 1 && valueString[0] === '0' && regexDig.test(valueString[1])) {
            prepared = prepared.slice(1);
        }

        const firstPointIndex = prepared.indexOf('.');

        if (firstPointIndex !== -1) {
            const integerPart = prepared.substring(0, firstPointIndex);
            const fractionalPart = prepared.substring(firstPointIndex + 1);
            const strippedFractionalPart = fractionalPart.replace(/\./g, '');
            prepared = integerPart + '.' + strippedFractionalPart;
        }

        return prepared || '';
    }

    firstUpdated() {
        super.firstUpdated();
        this._validationController.setInputHandlers();
        this._defaultValue = this.value = this.value || '';
        // Same as sotbit-input: formAssociatedCallback can run before the inner input ref exists.
        this._validationController.validate();
    }

    formAssociatedCallback(form) {
        this.form = form;
    }

    formDisabledCallback(disabled) {
        this.toggleAttribute('disabled', disabled);
    }

    formStateRestoreCallback(state) {
        this.value = state;
    }

    checkValidity(reportValidity) {
        return checkValidity(this, reportValidity);
    }

    focus(options) {
        this.inputRef.value?.focus(options);
    }

    _handleInput(e) {
        this.value = e.target.value;

        if (this.hasAttribute('error')) {
            this.removeAttribute('error');
            this._internals.checkValidity();
        }
    }

    _handleKeydown(e) {
        if (this.disableSubmit) {
            return;
        }

        if (e.defaultPrevented || e.key !== 'Enter') {
            return;
        }

        if (this.inputRef?.value) {
            this.value = this.inputRef.value.value;
        }

        if (this._internals?.form) {
            if (this._internals?.form.checkValidity()) {
                this._internals?.form.requestSubmit();
            } else {
                this._internals?.form.reportValidity();
            }
        }
    }

    _handleChange(e) {
        if (e.composed) {
            return;
        }

        if (!this.value) {
            this._setDefaultValue();
        }

        this.dispatchEvent(
            new CustomEvent('change', {
                bubbles: true,
                composed: true,
                detail: {
                    originalEvent: e,
                    value: this.value,
                },
            })
        );
    }

    _setDefaultValue() {
        this.value = this.initValue || '0';
        this.dispatchEvent(
            new CustomEvent('input', {
                bubbles: true,
                composed: true,
                detail: {
                    value: this.value,
                },
            })
        );
    }

    _handleClickToggle() {
        if (isNaN(Number(this.value))) {
            console.error(`${this.value} - is not number`);
            return;
        }

        this.value = Number(this.value) < 0 ? Math.abs(Number(this.value)) : -Number(this.value);

        this.dispatchEvent(
            new CustomEvent('input', {
                bubbles: true,
                composed: true,
                detail: {
                    value: this.value,
                },
            })
        );
    }

    render() {
        return html`
            <div
                class="input-field"
                @click="${this.focus}"
                @change="${this._handleChange}"
                @input="${this._handleInput}"
                @keydown="${this._handleKeydown}"
            >
                <div
                    class="input-container ${this.focused ? 'focused' : ''} ${this.disabled ? 'disabled' : ''} 
                        ${this.readonly ? 'readonly' : ''} ${this.placeholderShow ? 'placeholder' : ''}"
                    >
                        <div class="outline"></div>
                        <div class="left-group-container ${this.value < 0 ? 'negative' : 'positive'}">
                            ${this.value < 0 ? html`<slot name="toggle-btn-negative" @click="${this._handleClickToggle}"></slot>` : html`<slot name="toggle-btn-positive" @click="${this._handleClickToggle}"></slot>`}
                            
                        </div>
                        <div class="input-wrapper">
                            <input
                                type="text"
                                inputmode="numeric"
                                name="${this.name}"
                                ${ref(this.inputRef)}
                                .value="${live(this.value)}"
                                placeholder="${this.placeholder}"
                                ?required="${this.required}"
                                ?disabled="${this.disabled}"
                                ?readonly="${this.readonly}"
                                autocomplete="${this.autocomplete}"
                                ?autofocus="${this.autofocus}"
                                min="${this.min || nothing}"
                                slot="input"
                             />
                            ${
                                this.label && this.label.length > 0
                                    ? html` <label
                                          class="label ${this.smallLabel ? 'small' : ''} ${this.required
                                              ? 'required'
                                              : ''}"
                                          >${unsafeHTML(this.label)}</label
                                      >`
                                    : ''
                            }
                        </div>
                        
                        <div class="right-group-container" part="right-group-container">
                            <slot name="right-group-addon" ${ref(this.slotRef)} @slotchange="${this._handleSlotChange}"></slot>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}
